/**
 * Вход — логин и пароль. TOTP в фазе 1 нет (решение Ильи 23.08, 09 §11.1): он включается
 * вместе с TLS и внешним доступом в Б11, чтобы работа по второму фактору не делалась дважды.
 *
 * Ответ 423 (учётная запись заблокирована) показывается отдельно от 401: человеку нужно
 * знать, что дело не в пароле, а в том, что надо подождать.
 *
 * `100dvh`, А НЕ `100vh`, и поле у обёртки. На мобильных браузерах `vh` считается по окну
 * БЕЗ схлопывающейся адресной строки: карточка центрировалась по высоте, которой на экране
 * нет, и кнопка «Войти» уезжала под строку адреса. Поле в 16px нужно по той же причине, что
 * и `max-width: 100%` у `.card`: карточка задана шириной 380px, и на 360-пиксельном экране
 * она упиралась в оба края сразу — форма без единого поля читается как обрезанная страница.
 *
 * ЗНАК — ПОСЛЕДНЕГО ОТКРЫТОГО БРЕНДА (docs/multi-brand.md §6). До входа список брендов
 * недоступен (ручка за сессией), поэтому вид берётся из памяти браузера через провайдер
 * бренда (`brand.tsx`); памяти нет — GeoBoost, как было. Тема страницы — та же: её ставит
 * провайдер, а до первой отрисовки — `public/theme-boot.js`.
 */

import { type FormEvent, type JSX, useState } from 'react';
import { useLocation } from 'wouter';
import { ApiRequestError, post } from '../api/client.ts';
import { useBrands } from '../brand.tsx';
import { BrandMark } from '../components/BrandMark.tsx';
import { useSession } from '../session.tsx';

export function LoginPage(): JSX.Element {
  const { refresh } = useSession();
  const { look } = useBrands();
  const [, navigate] = useLocation();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    post<void>('/api/v1/auth/login', { login, password })
      .then(() => {
        refresh();
        navigate('/');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiRequestError && err.code === 'account_locked') {
          setError(
            'Учётная запись временно заблокирована после неудачных попыток. Подождите и попробуйте снова.',
          );
        } else if (err instanceof ApiRequestError && err.status === 401) {
          setError('Неверный логин или пароль.');
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div
      className="app"
      style={{
        minHeight: '100dvh',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <form
        className="card"
        onSubmit={onSubmit}
        style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <div className="brand" style={{ gap: 10 }}>
          <BrandMark look={look} size="login" />
        </div>
        <div>
          <div className="h2">Согласование постов</div>
          <div className="micro" style={{ marginTop: 4 }}>
            Доступ только у названных пользователей. Ни один пост не уходит в канал без вашего
            одобрения.
          </div>
        </div>
        <div>
          <label className="lbl" htmlFor="login">
            Логин
          </label>
          <input
            id="login"
            className="inp"
            autoComplete="username"
            value={login}
            onChange={(e) => {
              setLogin(e.target.value);
            }}
          />
        </div>
        <div>
          <label className="lbl" htmlFor="password">
            Пароль
          </label>
          <input
            id="password"
            className="inp"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
          />
        </div>
        {error === null ? null : (
          <div className="val val-err" role="alert">
            <span>{error}</span>
          </div>
        )}
        <button
          type="submit"
          className={
            busy || login === '' || password === '' ? 'btn btn-dis btn-w' : 'btn btn-pri btn-w'
          }
          disabled={busy || login === '' || password === ''}
        >
          {busy ? 'Входим…' : 'Войти'}
        </button>
      </form>
    </div>
  );
}
